import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { LogUtils } from "abweb/utils/log/LogUtils";
import $ from "jquery";

/**
 * Utility class that encapsulates a MutationObserver to watch for a visibility change on an element that may
 * have been caused by visibility changes on an ancestor element.
 * To use this class, create an instance of it with the element you want to watch and a callback function.
 * Then call enable() anytime you want. If the elementToWatch is hidden, this component will watch for when it toggles to becoming visible.
 * When it does so, it will call your callback method and then disable itself.
 * Be sure to call destroy when your component is destroyed or unmounted.
 */
export class MutationObserverForVisibility {
  /**
   * ctor
   * @param elementToWatch HTMLElement you want to watch for visibility changes from hidden to visible.
   * @param visibilityCallback Callback function to be called if your element toggles from hidden to visible
   */
  constructor(elementToWatch, visibilityCallback) {
    _defineProperty(this, "elementToWatch", void 0);
    _defineProperty(this, "visibilityCallback", void 0);
    _defineProperty(this, "mutationObserver", null);
    this.elementToWatch = $(elementToWatch);
    this.visibilityCallback = visibilityCallback;
  }

  /**
   * Call this method whenever you want to be notified if your elementToWatch is hidden and then transitions to visible again.
   * Note that in order for your callback method to get called, your elementToWatch must be currently hidden.
   * @return true if the elementToWatch is visible, false if it is hidden
   */
  enable() {
    if (this.mutationObserver) {
      // We are already enabled
      return false;
    }
    if (this.elementToWatch.is(":hidden")) {
      let hiddenElement = this.findElementThatIsMakingMeInvisible();
      if (hiddenElement == null) {
        MutationObserverForVisibility.LOG.warn(`enable Could not find ancestor hiddenElement???`);
        return true;
      }
      this.mutationObserver = this.createMutationObserver(hiddenElement);
      MutationObserverForVisibility.LOG.info(`enable Created MutationObserver to watch for visibility changes`);
      return false;
    }
    return true;
  }

  /**
   * Call this method to clean up resources when your component unmounts.
   */
  destroy() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }
  get isElementVisible() {
    return this.elementToWatch.is(":visible");
  }
  static isElementVisible(el) {
    return $(el).is(":visible");
  }
  findElementThatIsMakingMeInvisible() {
    let start = this.elementToWatch;
    let test = start;
    while (test.is(":hidden")) {
      // Check parent
      const parent = test.parent();
      if (parent.length === 0) {
        // Everybody is hidden
        return null;
      }
      if (parent.is(":visible")) {
        // We have our guy
        return test;
      }

      // Keep going up parent chain
      test = parent;
    }
    return null;
  }
  createMutationObserver(hiddenElement) {
    const mo = new MutationObserver(mutations => this.mutationObserverForVisibilityCallback(mutations));
    mo.observe(hiddenElement.get(0), {
      attributes: true,
      attributeFilter: ["style", "class"]
    });
    return mo;
  }
  mutationObserverForVisibilityCallback(mutations) {
    if (this.elementToWatch.is(":visible")) {
      // We are now visible
      if (this.mutationObserver == null) {
        // I don't think this should happen, but if it does, it means there must have been mutations in the queue that didn't get
        // flushed when we disconnected
        MutationObserverForVisibility.LOG.info("mutationObserverForVisibilityCallback MutationObserver has been disconnected already");
        return;
      }
      this.destroy();
      MutationObserverForVisibility.LOG.info("mutationObserverForVisibilityCallback elementToWatch is now visible");
      this.visibilityCallback();
    }
  }
}
_defineProperty(MutationObserverForVisibility, "LOG", LogUtils.getLogger(__filename));
//# sourceMappingURL=MutationObserverForVisibility.js.map
